import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import api from "services/api";
import { useStateContext } from "contexts/ContextProvider";
import CardDashboardText from "components/CardDashboardText";

// MUI components
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { Box, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import CardDashboardItem from "components/CardDashboardItem";
import { formatDateLong } from "helpers/ServiceHelpers";
import dayjs from "dayjs";
import ColumnFilter from "components/ColumnFilter";
import ColumnDateFilter from "components/ColumnDateFilter";
import DataGridHeader from "components/DataGridHeader";
import useColumnFilters from "hooks/useColumnFilters";
import useDataGridScrollRestore from "hooks/useDataGridScrollRestore";
import ClearIcon from "@mui/icons-material/ClearRounded";
import { Button } from "@mui/material";
import { GridToolbarContainer } from "@mui/x-data-grid";
import CEQuickFilter from "components/CEQuickFilter";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import TaskRoundedIcon from "@mui/icons-material/TaskRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import { OVERLAY_NAMES } from "constants/Overlays";
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";
import { usePresence } from "contexts/PresenceContext";
import { usePagePresence } from "hooks/useAssignmentPresence";

const DashboardTranslationsMonthly = ({ user, dateSelection }) => {
  // Default values in case dateSelection is incomplete
  const defaultMonth = dayjs().format("MMM");
  const defaultYear = dayjs().year();

  // Use the first month from dateSelection or default
  const displayMonth =
    dateSelection?.months && dateSelection.months.length > 0
      ? dateSelection.months[0]
      : defaultMonth;

  // Use the year from dateSelection or default
  const displayYear =
    dateSelection?.years && dateSelection.years.length > 0
      ? dateSelection.years[0]
      : defaultYear;

  const {
    refreshPage,
    showSnackbar,
    setOverlayData,
    setOverlayName,
  } = useStateContext();
  const theme = useTheme();

  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const { handleErrorDialog } = useStateContext();

  // Track last update for snackbar notification
  const [lastUpdate, setLastUpdate] = useState(null);

  // Set up entity tracking for Translations list to receive real-time updates
  const { setCurrentEntity } = usePresence();
  usePagePresence();

  useEffect(() => {
    // Set entity to TranslationsList so socket joins the entity room for list-level updates
    setCurrentEntity({
      entityType: "TranslationsList",
      entityId: "all",
    });

    return () => {
      // Clean up entity tracking when component unmounts
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [setCurrentEntity]);

  const [contextMenu, setContextMenu] = useState(null);
  const [selectedRow, setSelectedRow] = React.useState();
  const [listOfServices, setListOfServices] = useState([]);
  const [listOfClients, setListOfClients] = useState([]);
  const [listOfLinguists, setListOfLinguists] = useState([]);
  const [listOfLanguages, setListOfLanguages] = useState([]);
  const [listOfStatuses, setListOfStatuses] = useState([]);
  const [listOfTypes, setListOfTypes] = useState([]);
  const [rateTypes, setRateTypes] = useState([]);
  const [latestProjectNumber, setLatestProjectNumber] = useState(0);

  const [inProgressAssignments, setInProgressAssignments] = useState();
  const [deliveredAssignments, setDeliveredAssignments] = useState();

  // Preserve scroll positions across refetches (saves bounce to the top)
  const inProgressApiRef = useGridApiRef();
  const { capture: captureInProgressScroll, restore: restoreInProgressScroll } =
    useDataGridScrollRestore(inProgressApiRef);
  const deliveredApiRef = useGridApiRef();
  const { capture: captureDeliveredScroll, restore: restoreDeliveredScroll } =
    useDataGridScrollRestore(deliveredApiRef);

  const [assignmentsCounts, setAssignmentsCounts] = useState();

  const [openInProgressFilter, setOpenInProgressFilter] = useState(null);
  const [openDeliveredFilter, setOpenDeliveredFilter] = useState(null);
  const [inProgressFilterModel, setInProgressFilterModel] = useState({
    items: [],
  });
  const [deliveredFilterModel, setDeliveredFilterModel] = useState({
    items: [],
  });

  // Wrapper functions to close all other filters when one opens
  const handleSetOpenInProgressFilter = (value) => {
    if (value) {
      setOpenDeliveredFilter(null);
    }
    setOpenInProgressFilter(value);
  };

  const handleSetOpenDeliveredFilter = (value) => {
    if (value) {
      setOpenInProgressFilter(null);
    }
    setOpenDeliveredFilter(value);
  };

  // Wrapped clear handlers that also clear quick filters
  const handleClearInProgressFilters = () => {
    clearInProgressFilters();
    setInProgressFilterModel({ items: [] });
  };

  const handleClearDeliveredFilters = () => {
    clearDeliveredFilters();
    setDeliveredFilterModel({ items: [] });
  };

  const handleContextMenu = (event) => {
    event.preventDefault();

    setSelectedRow(Number(event.currentTarget.getAttribute("data-id")));
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null,
    );
  };

  const handleContextMenuEdit = () => {
    api
      .get(`/services/translations/${selectedRow}`)
      .then((response) => {
        const translationData = response.data;

        const secondData = {
          ...translationData,
          Action: OVERLAY_NAMES.TRANSLATION_ACTION_EDIT,
          Translations: listOfServices,
          Clients: listOfClients,
          Linguists: listOfLinguists,
          Languages: listOfLanguages,
          TranslationStatuses: listOfStatuses,
          TranslationTypes: listOfTypes,
          RateTypes: rateTypes,
        };

        setOverlayData(secondData);
        setOverlayName(OVERLAY_NAMES.TRANSLATION_NEW_EDIT);
        setContextMenu(null);
      })
      .catch((error) => {
        const message =
          error.response?.data?.message || "Failed to complete service.";
        handleErrorDialog(message, theme);
        showSnackbar(message, theme, "error");
      });
  };

  const handleContextMenuAddTask = () => {
    const translationData = listOfServices.find(({ id }) => id === selectedRow);

    setOverlayData({
      Action: OVERLAY_NAMES.TASK_ACTION_NEW,
      Task: {
        EntityType: "Service",
        EntityID: selectedRow,
        ServiceType: "Translation",
      },
      Service: {
        id: translationData.id,
        ProjectNumber: translationData.ProjectNumber,
        Details: translationData.Details,
        ServiceCategory: translationData.ServiceCategory,
      },
    });
    setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);
    setContextMenu(null);
  };

  const handleContextMenuComplete = () => {
    api
      .get(`/services/translations/${selectedRow}`)
      .then((response) => {
        const translationData = response.data;

        const data = {
          ...translationData,
          Action: OVERLAY_NAMES.TRANSLATION_ACTION_COMPLETE,
          Clients: listOfClients,
          Linguists: listOfLinguists,
          Languages: listOfLanguages,
          TranslationStatuses: listOfStatuses,
          TranslationTypes: listOfTypes,
          ProjectNumber: latestProjectNumber,
          RateTypes: rateTypes,
        };

        setOverlayData(data);
        setOverlayName(OVERLAY_NAMES.TRANSLATION_NEW_EDIT);
        setContextMenu(null);
      })
      .catch((error) => {
        const message =
          error.response?.data?.message || "Failed to complete service.";
        handleErrorDialog(message, theme);
        showSnackbar(message, theme, "error");
      });
  };

  const handleContextMenuCallRequestor = () => {
    const serviceData = listOfServices.find(({ id }) => id === selectedRow);

    const requestorPhones = serviceData?.Requestor?.PhoneNumbers || [];
    const requestorPrimary = requestorPhones.find((p) => p.IsPrimary) || requestorPhones[0];
    const requestorPhone = requestorPrimary?.Number || serviceData?.Requestor?.PhoneNumber;
    if (requestorPhone) {
      window.location.href = `tel:${requestorPhone.replace(/[^0-9+]/g, "")}`;
    }
    setContextMenu(null);
  };

  const handleContextMenuEmailRequestor = () => {
    const serviceData = listOfServices.find(({ id }) => id === selectedRow);

    if (serviceData?.Requestor?.Email) {
      const email = serviceData.Requestor.Email;
      const subject = "Regarding Assignment N° " + serviceData.ProjectNumber;
      const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(
        subject,
      )}`;
      window.open(mailtoLink, "_blank");
    }
    setContextMenu(null);
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  // Column definitions for filtering
  const filterColumns = useMemo(
    () => [
      {
        field: "ProjectNumber",
        valueGetter: (_, row) => row.ProjectNumber || "N/A",
      },
      {
        field: "Date",
        valueGetter: (_, row) => (row.Date ? formatDateLong(row.Date) : "N/A"),
      },
      {
        field: "Deadline",
        valueGetter: (_, row) =>
          row.Deadline ? dayjs.tz(row.Deadline).format("M/D/YYYY") : "N/A",
      },
      {
        field: "Client",
        valueGetter: (_, row) => row.Client?.Account || "N/A",
      },
      {
        field: "Requestor",
        valueGetter: (_, row) => row.Requestor?.Name || "N/A",
      },
      {
        field: "Linguist",
        valueGetter: (_, row) => row.Linguist?.Name || "N/A",
      },
      { field: "Details", valueGetter: (_, row) => row.Details || "N/A" },
      {
        field: "Language",
        valueGetter: (_, row) => row.Language?.Name || "N/A",
      },
      {
        field: "TargetLanguage",
        valueGetter: (_, row) => {
          const languages = row.ServiceTargetLanguages;
          if (!languages || languages.length === 0) return "N/A";
          return languages
            .map((item) =>
              item.Language && item.Language.Name ? item.Language.Name : "",
            )
            .filter((name) => name)
            .join(" | ");
        },
      },
    ],
    [],
  );

  const {
    columnFilters: inProgressFilters,
    filteredData: inProgressFilteredData,
    handleFilterChange: handleInProgressFilterChange,
    clearAllFilters: clearInProgressFilters,
    hasActiveFilters: inProgressHasActiveFilters,
  } = useColumnFilters(inProgressAssignments || [], filterColumns);

  // Cross-filtering data calculation for in-progress
  const inProgressFilterData = useMemo(() => {
    return filterColumns.map((column) => {
      const otherFilters = Object.entries(inProgressFilters).filter(
        ([field]) => field !== column.field,
      );

      let filteredData;
      if (otherFilters.length === 0) {
        filteredData = inProgressAssignments || [];
      } else {
        filteredData = (inProgressAssignments || []).filter((row) => {
          return otherFilters.every(([field, selectedValues]) => {
            if (!selectedValues || selectedValues.length === 0) return true;

            const filterColumn = filterColumns.find(
              (col) => col.field === field,
            );
            if (!filterColumn) return true;

            const value = filterColumn.valueGetter(null, row);
            return selectedValues.includes(value);
          });
        });
      }

      return filteredData;
    });
  }, [inProgressFilters, inProgressAssignments, filterColumns]);

  const {
    columnFilters: deliveredFilters,
    filteredData: deliveredFilteredData,
    handleFilterChange: handleDeliveredFilterChange,
    clearAllFilters: clearDeliveredFilters,
    hasActiveFilters: deliveredHasActiveFilters,
  } = useColumnFilters(deliveredAssignments || [], filterColumns);

  // Cross-filtering data calculation for delivered
  const deliveredFilterData = useMemo(() => {
    return filterColumns.map((column) => {
      const otherFilters = Object.entries(deliveredFilters).filter(
        ([field]) => field !== column.field,
      );

      let filteredData;
      if (otherFilters.length === 0) {
        filteredData = deliveredAssignments || [];
      } else {
        filteredData = (deliveredAssignments || []).filter((row) => {
          return otherFilters.every(([field, selectedValues]) => {
            if (!selectedValues || selectedValues.length === 0) return true;

            const filterColumn = filterColumns.find(
              (col) => col.field === field,
            );
            if (!filterColumn) return true;

            const value = filterColumn.valueGetter(null, row);
            return selectedValues.includes(value);
          });
        });
      }

      return filteredData;
    });
  }, [deliveredFilters, deliveredAssignments, filterColumns]);

  // Check for quick filters on both grids
  const inProgressHasQuickFilter =
    inProgressFilterModel?.quickFilterValues?.length > 0;
  const inProgressHasAnyActiveFilters =
    inProgressHasActiveFilters || inProgressHasQuickFilter;

  const deliveredHasQuickFilter =
    deliveredFilterModel?.quickFilterValues?.length > 0;
  const deliveredHasAnyActiveFilters =
    deliveredHasActiveFilters || deliveredHasQuickFilter;

  // Fetch all translations data for the month
  const fetchMonthlyData = useCallback(() => {
    // Just pass the dateSelection directly to the server
    const params = dateSelection || {};

    setIsLoading(true);
    captureInProgressScroll();
    captureDeliveredScroll();

    api
      .all([
        api.get("/dashboard/translations/monthly/inprogress", {
          params: params,
        }),
        api.get("/dashboard/translations/monthly/delivered", {
          params: params,
        }),
        api.get("/dashboard/translations/monthly/counts", {
          params: params,
        }),
      ])
      .then(
        api.spread((requestedResponse, confirmedResponse, countsResponse) => {
          if (requestedResponse) {
            setInProgressAssignments(requestedResponse.data);
            restoreInProgressScroll(requestedResponse.data);
          }
          if (confirmedResponse) {
            setDeliveredAssignments(confirmedResponse.data);
            restoreDeliveredScroll(confirmedResponse.data);
          }
          if (countsResponse) {
            setAssignmentsCounts(countsResponse.data);
          }

          setIsLoading(false);
        }),
      )
      .catch((error) => {
        const message =
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.";
        handleErrorDialog(message, theme);
        setIsLoading(false);
      });
  }, [
    dateSelection,
    handleErrorDialog,
    theme,
    captureInProgressScroll,
    restoreInProgressScroll,
    captureDeliveredScroll,
    restoreDeliveredScroll,
  ]);

  useEffect(() => {
    fetchMonthlyData();
  }, [refreshPage, fetchMonthlyData]);

  // Listener callback for translations list refetch
  const handleTranslationsMonthlyRefetch = useCallback(
    (updatedBy) => {
      setLastUpdate(updatedBy); // Store for snackbar
      fetchMonthlyData(); // Refetch data
    },
    [fetchMonthlyData],
  );

  // Show snackbar when update is received (in separate effect to avoid listener churn)
  useEffect(() => {
    if (lastUpdate?.userName) {
      const message = lastUpdate?.projectNumber
        ? `#${lastUpdate.projectNumber} updated by ${lastUpdate.userName}`
        : `Translation updated by ${lastUpdate.userName}`;
      showSnackbar(message, theme, "info");
      setLastUpdate(null);
    }
  }, [lastUpdate, showSnackbar, theme]);

  useAssignmentUpdates({
    modelType: "TranslationsList",
    recordId: "all",
    onUpdate: handleTranslationsMonthlyRefetch,
  });

  useEffect(() => {
    api
      .all([
        api.get("/clients/brief"),
        api.get("/linguists/brief"),
        api.get("/languages"),
        api.get("/services/translations/statuses"),
        api.get("/services/translations/types"),
        api.get("/services/translations/latestprojectnumber"),
        api.get("/services/translations/ratetypes"),
      ])
      .then(
        api.spread(
          (
            clientsResponse,
            linguistsResponse,
            languagesResponse,
            statusesResponse,
            typesResponse,
            projectNumberResponse,
            rateTypesResponse,
          ) => {
            setListOfClients(clientsResponse?.data || []);
            setListOfLinguists(linguistsResponse?.data || []);
            setListOfLanguages(languagesResponse?.data || []);
            setListOfStatuses(statusesResponse?.data || []);
            setListOfTypes(typesResponse?.data || []);
            setLatestProjectNumber(projectNumberResponse?.data || 0);
            setRateTypes(rateTypesResponse?.data || []);
          },
        ),
      )
      .catch((error) => {
        const message =
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.";
        handleErrorDialog(message, theme);
      });
  }, [handleErrorDialog, theme]);

  useEffect(() => {
    const allServices = [
      ...(inProgressAssignments || []),
      ...(deliveredAssignments || []),
    ];
    setListOfServices(allServices);
  }, [inProgressAssignments, deliveredAssignments]);

  const dateComparator = (v1, v2) => {
    if (v1 === "N/A") return 1;
    if (v2 === "N/A") return -1;
    return dayjs(v1, "M/D/YYYY").valueOf() - dayjs(v2, "M/D/YYYY").valueOf();
  };

  const getColumns = (
    columnFilters,
    handleFilterChange,
    filterData,
    openFilter,
    setOpenFilter,
  ) => [
    {
      field: "ProjectNumber",
      headerName: "Number",
      minWidth: 120,
      valueGetter: (_, params) => {
        const data = params.ProjectNumber;
        const value = data ? data : "N/A";
        return value;
      },
      flex: 1.2,
      renderHeader: () => (
        <DataGridHeader
          headerName="Number"
          children={
            <ColumnFilter
              field="ProjectNumber"
              data={filterData[0] || []}
              onFilterChange={(values) =>
                handleFilterChange("ProjectNumber", values)
              }
              selectedValues={columnFilters.ProjectNumber || []}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Date",
      headerName: "Date",
      valueGetter: (_, params) => {
        const data = params.Date;
        const value = data ? formatDateLong(data) : "N/A";
        return value;
      },
      sortComparator: dateComparator,
      minWidth: 180,
      flex: 1.8,
      renderHeader: () => (
        <DataGridHeader
          headerName="Date"
          children={
            <ColumnDateFilter
              field="Date"
              data={filterData[1] || []}
              onFilterChange={(values) => handleFilterChange("Date", values)}
              selectedValues={columnFilters.Date || []}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Deadline",
      headerName: "Deadline",
      valueGetter: (_, params) => {
        const data = params.Deadline;
        const value = data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
        return value;
      },
      sortComparator: dateComparator,
      minWidth: 130,
      flex: 1.3,
      renderHeader: () => (
        <DataGridHeader
          headerName="Deadline"
          children={
            <ColumnDateFilter
              field="Deadline"
              data={filterData[2] || []}
              onFilterChange={(values) =>
                handleFilterChange("Deadline", values)
              }
              selectedValues={columnFilters.Deadline || []}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Client",
      headerName: "Client",
      valueGetter: (_, params) => {
        const data = params.Client;
        const value = data ? data.Account : "N/A";
        return value;
      },
      minWidth: 180,
      flex: 1.8,
      renderHeader: () => (
        <DataGridHeader
          headerName="Client"
          children={
            <ColumnFilter
              field="Client"
              data={filterData[3] || []}
              onFilterChange={(values) => handleFilterChange("Client", values)}
              selectedValues={columnFilters.Client || []}
              valueGetter={(_, params) => params.Client?.Account || "N/A"}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Requestor",
      headerName: "Requestor",
      valueGetter: (_, params) => {
        const data = params.Requestor;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 150,
      flex: 1.5,
      renderHeader: () => (
        <DataGridHeader
          headerName="Requestor"
          children={
            <ColumnFilter
              field="Requestor"
              data={filterData[4] || []}
              onFilterChange={(values) =>
                handleFilterChange("Requestor", values)
              }
              selectedValues={columnFilters.Requestor || []}
              valueGetter={(_, params) => params.Requestor?.Name || "N/A"}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Linguist",
      headerName: "Linguist",
      valueGetter: (_, params) => {
        const data = params.Linguist;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 140,
      flex: 1.4,
      renderHeader: () => (
        <DataGridHeader
          headerName="Linguist"
          children={
            <ColumnFilter
              field="Linguist"
              data={filterData[5] || []}
              onFilterChange={(values) =>
                handleFilterChange("Linguist", values)
              }
              selectedValues={columnFilters.Linguist || []}
              valueGetter={(_, params) => params.Linguist?.Name || "N/A"}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Details",
      headerName: "Details",
      valueGetter: (_, params) => {
        const data = params.Details;
        const value = data ? data : "N/A";
        return value;
      },
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      minWidth: 450,
      flex: 4.5,
      renderHeader: () => (
        <DataGridHeader
          headerName="Details"
          children={
            <ColumnFilter
              field="Details"
              data={filterData[6] || []}
              onFilterChange={(values) => handleFilterChange("Details", values)}
              selectedValues={columnFilters.Details || []}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Language",
      headerName: "Source Language",
      valueGetter: (_, params) => {
        const data = params.Language;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 175,
      flex: 1.75,
      renderHeader: () => (
        <DataGridHeader
          headerName="Source Language"
          children={
            <ColumnFilter
              field="Language"
              data={filterData[7] || []}
              onFilterChange={(values) =>
                handleFilterChange("Language", values)
              }
              selectedValues={columnFilters.Language || []}
              valueGetter={(_, params) => params.Language?.Name || "N/A"}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "TargetLanguage",
      headerName: "Target Languages",
      valueGetter: (_, params) => {
        const languages = params.ServiceTargetLanguages;
        if (!languages || languages.length === 0) return "N/A";
        // Extract the Language.Name from each item and filter out any empty values
        return languages
          .map((item) =>
            item.Language && item.Language.Name ? item.Language.Name : "",
          )
          .filter((name) => name)
          .join(" | ");
      },
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      minWidth: 300,
      flex: 3.0,
      renderHeader: () => (
        <DataGridHeader
          headerName="Target Languages"
          children={
            <ColumnFilter
              field="TargetLanguage"
              data={filterData[8] || []}
              onFilterChange={(values) =>
                handleFilterChange("TargetLanguage", values)
              }
              selectedValues={columnFilters.TargetLanguage || []}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
  ];

  const CustomToolbar = useMemo(() => {
    const Toolbar = ({ hasAnyActiveFilters, onClear, title }) => {
      return (
        <GridToolbarContainer
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          {title && (
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: theme.palette.primary.text2,
                margin: 0,
                padding: 0,
                alignSelf: "flex-start",
              }}
            >
              {title}
            </Typography>
          )}

          <Box
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
            }}
          >
            {hasAnyActiveFilters && (
              <Button
                startIcon={<ClearIcon />}
                onClick={onClear}
                variant="outlined"
                size="small"
                sx={{
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                Clear Filters
              </Button>
            )}
          </Box>

          <Box sx={{ flex: 1 }} />

          <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
            <CEQuickFilter />
          </Box>
        </GridToolbarContainer>
      );
    };

    return Toolbar;
  }, [
    theme.palette.background.default,
    theme.palette.primary.buttonHoverSelected,
    theme.palette.primary.main,
    theme.palette.primary.text2,
  ]);

  const getDataGrid = (
    title,
    listOfServices,
    filteredData,
    columnFilters,
    handleFilterChange,
    clearAllFilters,
    hasAnyActiveFilters,
    openFilter,
    setOpenFilter,
    filterData,
    filterModel,
    onFilterModelChange,
    apiRef,
  ) => {
    return (
      <DataGrid
        loading={isLoading}
        apiRef={apiRef}
        initialState={{
          pagination: { paginationModel: { pageSize: 5 } },
        }}
        pageSizeOptions={[5, 10, 25, { value: -1, label: "All" }]}
        getRowId={(row) => row.id}
        rows={filteredData || []}
        columns={getColumns(
          columnFilters,
          handleFilterChange,
          filterData || [],
          openFilter,
          setOpenFilter,
        )}
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        rowSelectionModel={
          openFilter || openInProgressFilter || openDeliveredFilter
            ? []
            : undefined
        }
        disableRowSelectionOnClick={
          !!(openFilter || openInProgressFilter || openDeliveredFilter)
        }
        disableColumnSorting
        onRowClick={(params, event, details) => {
          if (openFilter) {
            setOpenFilter(null);
            return;
          }

          // Close all other filters
          if (openInProgressFilter) {
            handleSetOpenInProgressFilter(null);
            return;
          }
          if (openDeliveredFilter) {
            handleSetOpenDeliveredFilter(null);
            return;
          }

          //to do
          const data = {
            ComingFrom: "/dashboard",
            Translation: params.row,
            TranslationID: params.row.id,
          };

          setOverlayData(data);

          navigate(`/translations/${params.row.id}`, { state: data });
        }}
        getCellClassName={(params) => {
          return params.value === "✘" ||
            params.value === "N/A" ||
            params.value === "0%" ||
            params.value === "null"
            ? "notAvailable"
            : "";
        }}
        slots={{
          toolbar: CustomToolbar,
        }}
        slotProps={{
          row: {
            onContextMenu: handleContextMenu,
            style: { cursor: "context-menu" },
          },
          loadingOverlay: {
            variant: "skeleton",
            noRowsVariant: "skeleton",
          },
          toolbar: {
            hasAnyActiveFilters,
            onClear: clearAllFilters,
            title,
          },
        }}
        disableColumnMenu
        ignoreDiacritics
        sx={{
          backgroundColor: theme.palette.background.default,
          borderRadius: "0px",
          boxShadow: "none",
          height: "100%",
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "bold",
            color: theme.palette.primary.main,
          },
          "& .MuiDataGrid-scrollbarFiller": {
            backgroundColor: theme.palette.background.alt,
          },
          "& .MuiDataGrid-menuIconButton": {
            opacity: 1,
            color: theme.palette.primary.main,
          },
          "& .MuiDataGrid-columnSeparator": {
            color: theme.palette.primary.main,
          },
          "& .MuiButton-root": {
            color: theme.palette.primary.main,
          },
          "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
            outline: "none !important",
          },
          "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
            {
              outline: "none !important",
            },
          "& .MuiDataGrid-scrollbar": {
            margin: "0 0 0 0",
          },
          "& .MuiDataGrid-columnHeaderTitleContainerContent": {
            flex: 1,
          },
          "& .MuiTypography-root.MuiTypography-body1": {
            textAlign: "left !important",
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-root[aria-label*='Export']":
            {
              display: "none !important",
            },
        }}
      />
    );
  };

  // Generate title for the month display
  const getMonthsTitle = () => {
    if (dateSelection?.months?.length === 1) {
      return displayMonth;
    } else if (dateSelection?.months?.length > 1) {
      return `Multiple Months (${dateSelection.months.length})`;
    }
    return "No Month Selected";
  };

  return (
    <Grid container spacing={2} size={{ xs: 12 }}>
      {/* First Row: Three Cards */}
      <Grid container spacing={2} size={{ xs: 12 }} sx={{ px: 1, pt: 1 }}>
        <Grid size={{ xs: 12, sm: 12 / 4 }}>
          <CardDashboardText
            title="In-Progress"
            content={assignmentsCounts?.inProgress || (isLoading ? "" : "0")}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 / 4 }}>
          <CardDashboardText
            title="Delivered"
            content={assignmentsCounts?.delivered || (isLoading ? "" : "0")}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 / 4 }}>
          <CardDashboardText
            title="Completed"
            content={assignmentsCounts?.completed || (isLoading ? "" : "0")}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 / 4 }}>
          <CardDashboardText
            title="Total"
            content={assignmentsCounts?.all || (isLoading ? "" : "0")}
            isLoading={isLoading}
          />
        </Grid>
      </Grid>

      {/* Second Row: Two Grids with DataGrid components  */}
      <Grid container spacing={2} size={{ xs: 12 }} sx={{ px: 1, pb: 1 }}>
        <Grid size={{ xs: 12 }} sx={{ display: "flex", minHeight: 0 }}>
          <CardDashboardItem
            height="100%"
            title={null}
            content={
              inProgressAssignments?.length > 0 || isLoading
                ? getDataGrid(
                    `In-Progress Assignments - ${getMonthsTitle()} ${displayYear}`,
                    inProgressAssignments,
                    inProgressFilteredData,
                    inProgressFilters,
                    handleInProgressFilterChange,
                    handleClearInProgressFilters,
                    inProgressHasAnyActiveFilters,
                    openInProgressFilter,
                    handleSetOpenInProgressFilter,
                    inProgressFilterData,
                    inProgressFilterModel,
                    setInProgressFilterModel,
                    inProgressApiRef,
                  )
                : `No In-Progress Assignments for ${getMonthsTitle()} ${displayYear}.`
            }
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ display: "flex", minHeight: 0 }}>
          <CardDashboardItem
            height="100%"
            title={null}
            content={
              deliveredAssignments?.length > 0 || isLoading
                ? getDataGrid(
                    `Delivered Assignments - ${getMonthsTitle()} ${displayYear}`,
                    deliveredAssignments,
                    deliveredFilteredData,
                    deliveredFilters,
                    handleDeliveredFilterChange,
                    handleClearDeliveredFilters,
                    deliveredHasAnyActiveFilters,
                    openDeliveredFilter,
                    handleSetOpenDeliveredFilter,
                    deliveredFilterData,
                    deliveredFilterModel,
                    setDeliveredFilterModel,
                    deliveredApiRef,
                  )
                : `No Delivered Assignments for ${getMonthsTitle()} ${displayYear}.`
            }
            isLoading={isLoading}
          />
        </Grid>
      </Grid>

      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        slotProps={{
          paper: {
            sx: {
              bgcolor: theme.palette.background.alt,
              border: `1px solid ${theme.palette.divider}`,
              "& .MuiListItemIcon-root": {
                color: theme.palette.primary.main,
              },
            },
          },
        }}
      >
        <MenuItem onClick={handleContextMenuEdit}>
          <ListItemIcon>
            <EditRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleContextMenuComplete}>
          <ListItemIcon>
            <TaskRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Complete</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleContextMenuAddTask}>
          <ListItemIcon>
            <AddTaskRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Add Task</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleContextMenuCallRequestor}>
          <ListItemIcon>
            <CallRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Call Requestor</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleContextMenuEmailRequestor}>
          <ListItemIcon>
            <EmailRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Email Requestor</ListItemText>
        </MenuItem>
      </Menu>
    </Grid>
  );
};

export default DashboardTranslationsMonthly;
